import {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  type ElementType,
} from 'react';

type ScrollingTitleProps = {
  text: string;
  className?: string;
  as?: ElementType;
  id?: string;
  /** When false, renders without marquee measurement / animation. */
  enabled?: boolean;
  /**
   * When false, pause the marquee in place (e.g. mini tray hidden under sheet).
   * Resume from the same playhead when true again — avoids a full restart stall.
   */
  active?: boolean;
  /**
   * When false, ignore VT “park at rest” (persistent mini chrome).
   * Page/catalog titles keep the default so forward nav snapshots stay clean.
   */
  resetOnViewTransition?: boolean;
};

/** Gap between the two title copies in the loop (iOS Music–like spacer). */
const LOOP_GAP_PX = 32;
/**
 * Default fade gutter (px) when CSS `--scroll-fade` is unset.
 * Hosts override via CSS (e.g. footer mini title uses a shorter gutter so
 * the fade stays in the art→title gap and doesn’t wash over artwork).
 */
const FADE_PX = 20;

/** Resolve CSS `--scroll-fade` from the title host (inherits into the clip). */
function readFadePx(host: HTMLElement): number {
  const raw = getComputedStyle(host).getPropertyValue('--scroll-fade').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : FADE_PX;
}
/** Scroll speed while moving (px/s). */
const SPEED_PX_PER_SEC = 40;
/** Hold at the start of each loop so the leading edge can be read. */
const PAUSE_START_SEC = 1.75;
/** Brief hold after a full cycle before the seamless reset. */
const PAUSE_END_SEC = 0.85;
/** Enter overflow when text exceeds readable clip by this much. */
const OVERFLOW_ENTER_PX = 4;
/** Leave overflow only when fully fitting (hysteresis — stops RO flicker). */
const OVERFLOW_EXIT_PX = 0;
/** Ignore segment jitter below this while an animation is running. */
const SEGMENT_STABLE_PX = 2;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

type Metrics = {
  text: string;
  overflow: number;
  segment: number;
};

type AnimWithSegment = Animation & { __segment?: number };

/**
 * iOS Music–style title: when text overflows, infinite horizontal loop
 * (duplicated string + gap) with pauses at the start/end of each cycle.
 *
 * Fade gutter overhangs into the parent padding (not an inset on the title).
 * Resting glyphs stay flush with the content edge; only scrolled glyphs fade.
 */
export function ScrollingTitle({
  text,
  className,
  as: Comp = 'span',
  id,
  enabled = true,
  active = true,
  resetOnViewTransition = true,
}: ScrollingTitleProps) {
  const hostRef = useRef<HTMLElement | null>(null);
  const clipRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const trackRef = useRef<HTMLSpanElement>(null);
  const animRef = useRef<AnimWithSegment | null>(null);
  const metricsRef = useRef<Metrics>({ text: '', overflow: 0, segment: 0 });
  const loopingRef = useRef(false);
  const textRef = useRef(text);
  textRef.current = text;
  const activeRef = useRef(active);
  activeRef.current = active;
  const resetOnVtRef = useRef(resetOnViewTransition);
  resetOnVtRef.current = resetOnViewTransition;
  const measureNowRef = useRef<() => void>(() => {});

  const [metrics, setMetrics] = useState<Metrics>({
    text: '',
    overflow: 0,
    segment: 0,
  });

  const stopAnim = () => {
    animRef.current?.cancel();
    animRef.current = null;
    const track = trackRef.current;
    if (track) {
      track.getAnimations().forEach((a) => a.cancel());
      track.style.transform = '';
    }
  };

  useLayoutEffect(() => {
    stopAnim();

    if (!enabled) {
      const next = { text, overflow: 0, segment: 0 };
      metricsRef.current = next;
      loopingRef.current = false;
      setMetrics(next);
      measureNowRef.current = () => {};
      return;
    }

    const cleared = { text: '', overflow: 0, segment: 0 };
    metricsRef.current = cleared;
    loopingRef.current = false;
    setMetrics(cleared);

    const host = hostRef.current;
    const clip = clipRef.current;
    const measure = measureRef.current;
    if (!host || !clip || !measure) return;

    let cancelled = false;
    let ro: ResizeObserver | null = null;
    let raf = 0;

    const applyIfChanged = (next: Metrics) => {
      if (next.text !== textRef.current) return;
      const prev = metricsRef.current;
      const wasLooping = loopingRef.current;

      let overflow = next.overflow;
      if (wasLooping && prev.text === next.text) {
        overflow = overflow > OVERFLOW_EXIT_PX ? overflow : 0;
      } else if (overflow < OVERFLOW_ENTER_PX) {
        overflow = 0;
      }

      const segment = next.segment;
      const same =
        prev.text === next.text &&
        Math.abs(prev.overflow - overflow) < 1 &&
        Math.abs(prev.segment - segment) < SEGMENT_STABLE_PX;

      if (
        same ||
        (wasLooping &&
          prev.text === next.text &&
          overflow > 0 &&
          Math.abs(prev.segment - segment) < SEGMENT_STABLE_PX)
      ) {
        return;
      }

      const resolved = { text: next.text, overflow, segment };
      metricsRef.current = resolved;
      loopingRef.current = overflow > 0 && segment > 0;
      setMetrics(resolved);
    };

    const measureNow = () => {
      if (cancelled) return;
      // Hidden mini tray (sheet open) — don’t clobber metrics with a 0-width clip.
      if (!activeRef.current) return;
      const textW = measure.offsetWidth;
      // Clip includes the left gutter; readable width excludes it.
      const clipW = Math.max(0, clip.clientWidth - readFadePx(host));
      if (clipW <= 0 || textW <= 0) return;
      const delta = Math.max(0, textW - clipW);
      applyIfChanged({
        text,
        overflow: delta,
        segment: textW + LOOP_GAP_PX,
      });
    };
    measureNowRef.current = measureNow;

    const scheduleMeasure = () => {
      // VT morph freezes named chrome as snapshots; remasuring mid-morph can
      // restart the marquee into PAUSE_START and looks like a long stall.
      if (document.documentElement.dataset.vtDir) return;
      if (!activeRef.current) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (document.documentElement.dataset.vtDir) return;
        if (!activeRef.current) return;
        measureNow();
      });
    };

    const start = async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch {
        /* ignore */
      }
      if (cancelled) return;
      await new Promise<void>((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r())),
      );
      if (cancelled) return;
      measureNow();
      ro = new ResizeObserver(scheduleMeasure);
      ro.observe(clip);
      ro.observe(measure);
    };

    void start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
      measureNowRef.current = () => {};
      stopAnim();
    };
  }, [text, enabled]);

  // Sheet dismiss / tray unhide — remasure once active again.
  useEffect(() => {
    if (!enabled || !active) return;
    measureNowRef.current();
  }, [active, enabled]);

  const looping =
    enabled &&
    metrics.text === text &&
    metrics.overflow > 0 &&
    metrics.segment > 0;

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !looping || prefersReducedMotion()) {
      stopAnim();
      return;
    }

    const segment = metrics.segment;

    const scrollSec = Math.max(2.5, segment / SPEED_PX_PER_SEC);
    const totalSec = PAUSE_START_SEC + scrollSec + PAUSE_END_SEC;
    const durationMs = totalSec * 1000;
    const startHold = PAUSE_START_SEC / totalSec;
    const endHold = (PAUSE_START_SEC + scrollSec) / totalSec;
    const dist = `-${segment}px`;

    const keyframes: Keyframe[] = [
      { transform: 'translate3d(0,0,0)', offset: 0 },
      { transform: 'translate3d(0,0,0)', offset: startHold },
      { transform: `translate3d(${dist},0,0)`, offset: endHold },
      { transform: `translate3d(${dist},0,0)`, offset: 1 },
    ];

    const startOrResume = (phaseMs = 0) => {
      const running = animRef.current;
      if (
        running &&
        (running.playState === 'running' || running.playState === 'paused') &&
        running.__segment === segment
      ) {
        if (running.playState === 'paused') running.play();
        return;
      }

      let resumeMs = phaseMs;
      if (
        !resumeMs &&
        running &&
        running.__segment &&
        running.__segment > 0 &&
        typeof running.currentTime === 'number'
      ) {
        const prevDur =
          (PAUSE_START_SEC +
            Math.max(2.5, running.__segment / SPEED_PX_PER_SEC) +
            PAUSE_END_SEC) *
          1000;
        resumeMs = ((running.currentTime % prevDur) / prevDur) * durationMs;
      }

      stopAnim();

      const anim = track.animate(keyframes, {
        duration: durationMs,
        iterations: Infinity,
        easing: 'linear',
      }) as AnimWithSegment;
      anim.__segment = segment;
      if (resumeMs > 0) anim.currentTime = resumeMs;
      animRef.current = anim;
    };

    /** Browser may cancel WAAPI while the tray is visibility:hidden. */
    const ensureRunning = () => {
      if (!activeRef.current) return;
      const running = animRef.current;
      if (
        running &&
        (running.playState === 'running' || running.playState === 'paused') &&
        running.__segment === segment
      ) {
        if (running.playState === 'paused') running.play();
        return;
      }
      // Skip the start hold so sheet→mini doesn’t look stalled for ~1.75s.
      startOrResume(PAUSE_START_SEC * 1000);
    };

    if (!active) {
      animRef.current?.pause();
      return;
    }

    startOrResume();

    /**
     * After VT: forward parks page marquees at rest → restart from 0.
     * Persistent mini chrome opts out via resetOnViewTransition={false}.
     * Back keeps playhead → resume in place (rebuild only if browser cancelled).
     */
    const onVtFinished = (event: Event) => {
      const detail = (event as CustomEvent<{ resetToRest?: boolean }>).detail;
      const resetToRest =
        detail?.resetToRest === true && resetOnVtRef.current;
      const running = animRef.current;
      if (
        running &&
        (running.playState === 'running' || running.playState === 'paused') &&
        running.__segment === segment
      ) {
        if (resetToRest) running.currentTime = 0;
        if (running.playState === 'paused') running.play();
        return;
      }
      // Opted-out chrome: rebuild mid-cycle if the browser cancelled the anim.
      if (!resetOnVtRef.current) {
        startOrResume(PAUSE_START_SEC * 1000);
        return;
      }
      startOrResume(resetToRest ? 0 : PAUSE_START_SEC * 1000);
    };
    window.addEventListener('zg:vt-finished', onVtFinished);

    const host = hostRef.current;
    let io: IntersectionObserver | null = null;
    if (host && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) ensureRunning();
        },
        { threshold: 0 },
      );
      io.observe(host);
    }

    return () => {
      window.removeEventListener('zg:vt-finished', onVtFinished);
      io?.disconnect();
      // Pause (don’t cancel) so the next active cycle can resume the playhead.
      animRef.current?.pause();
    };
  }, [looping, metrics.segment, text, active]);

  if (!enabled) {
    return (
      <Comp id={id} className={className} title={text}>
        {text}
      </Comp>
    );
  }

  return (
    <Comp
      ref={hostRef}
      id={id}
      className={[
        'scrolling-title',
        looping ? 'is-overflow' : 'is-static',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={text}
    >
      <span ref={clipRef} className="scrolling-title-clip">
        <span ref={trackRef} className="scrolling-title-track">
          <span ref={measureRef} className="scrolling-title-text">
            {text}
          </span>
          {looping ? (
            <>
              <span
                className="scrolling-title-gap"
                style={{ width: LOOP_GAP_PX }}
                aria-hidden
              />
              <span className="scrolling-title-text" aria-hidden>
                {text}
              </span>
            </>
          ) : null}
        </span>
      </span>
    </Comp>
  );
}
